import React from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import { IconLogout2 } from '@tabler/icons-react';
import { navTransactionLabel, useNavTransactions } from 'helpers/useNavTransactions';

import { navTransactionIcon } from './navTransactionIcon';

interface AccountMenuProps {
  isExpanded: boolean;
  isLoggedIn: boolean;
}

const HELP_CENTER_URL = 'https://help.opendoor.com';

/**
 * The signed-in dropdown lists the customer's active transactions above a static
 * footer (Account, Help center, Sign out).
 */
const AccountMenu = ({ isExpanded, isLoggedIn }: AccountMenuProps) => {
  if (!isLoggedIn) {
    return (
      <PrimaryNavSubmenu isExpanded={isExpanded} isLoggedIn={false}>
        <PrimaryNavItem>
          <MenuItem href={HELP_CENTER_URL} ariaLabel="Help center">
            Help center
          </MenuItem>
        </PrimaryNavItem>
      </PrimaryNavSubmenu>
    );
  }

  return (
    <PrimaryNavSubmenu isExpanded={isExpanded} isLoggedIn>
      <ActiveTransactionItems />
      <AccountMenuFooter />
    </PrimaryNavSubmenu>
  );
};

/** The customer's active purchases and sales. */
const ActiveTransactionItems: React.FC = () => {
  const transactions = useNavTransactions();

  return (
    <>
      {transactions.map((transaction) => {
        const Icon = navTransactionIcon(transaction.type);
        const label = navTransactionLabel(transaction);
        return (
          <PrimaryNavItem key={transaction.id}>
            <MenuItem
              href={transaction.href}
              ariaLabel={label}
              icon={<Icon size={20} stroke={1.5} />}
            >
              {label}
            </MenuItem>
          </PrimaryNavItem>
        );
      })}
    </>
  );
};

/** Account, Help center, and Sign out — shown beneath the active transactions. */
const AccountMenuFooter: React.FC = () => {
  const { logout } = useAuth();

  return (
    <>
      <PrimaryNavItem>
        <MenuItem href="/account" ariaLabel="Account">
          Account
        </MenuItem>
      </PrimaryNavItem>
      <Divider />
      <PrimaryNavItem>
        <MenuItem href={HELP_CENTER_URL} ariaLabel="Help center">
          Help center
        </MenuItem>
      </PrimaryNavItem>
      <PrimaryNavItem>
        <MenuButton
          onPress={() => logout()}
          label="Sign out"
          icon={<IconLogout2 size={20} stroke={1.5} />}
          analyticsName="cosmos-account-menu-sign-out"
        />
      </PrimaryNavItem>
    </>
  );
};

const PrimaryNavSubmenu: React.FC<{
  isExpanded: boolean;
  isLoggedIn: boolean;
  children: React.ReactNode;
}> = ({ isExpanded, isLoggedIn, children }) => (
  // `inert` removes the collapsed menu from focus/pointer interaction during the
  // fade, complementing aria-hidden and visibility. Spread conditionally because
  // `inert` isn't typed as a JSX prop on this React version.
  <Col
    {...(!isExpanded ? { inert: true } : {})}
    role="list"
    aria-hidden={!isExpanded}
    className={cn(
      // top-100% clears the avatar; +12px covers the remaining space between the
      // avatar bottom and the header bar bottom (avatar is vertically centered).
      'absolute top-[calc(100%+12px)] right-0 z-[1000] gap-1 overflow-hidden rounded-xl border border-od-border-primary bg-od-bg-primary p-1 transition-[opacity,visibility] duration-200',
      isLoggedIn ? 'min-w-[268px] max-w-[268px]' : 'min-w-[198px] max-w-[270px]',
      isExpanded ? 'visible opacity-100' : 'invisible opacity-0',
    )}
  >
    {children}
  </Col>
);

const PrimaryNavItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box role="listitem" className="w-full">
    {children}
  </Box>
);

// Decorative only (aria-hidden) so it doesn't break the submenu's list/listitem
// accessibility tree, which must contain listitems exclusively.
const Divider = () => <Box aria-hidden className="h-px w-full bg-od-border-secondary" />;

// Shared row geometry so every menu row — link or button, with an icon or
// without — is the same height and aligns to one text column. The 20px icon
// column is always reserved (rendered empty when there's no icon) so rows are
// never offset by the items that happen to have icons.
const MENU_ROW_CLASSES =
  'w-full cursor-pointer flex-row items-center gap-2 rounded-lg py-2 pr-3 pl-2 text-od-content-primary no-underline hover:bg-od-bg-hover-primary';

const MenuRowIcon: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <Box aria-hidden className="h-5 w-5 items-center justify-center">
    {children}
  </Box>
);

const MenuRowLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text as="span" className="min-w-0 truncate text-body-md text-inherit">
    {children}
  </Text>
);

interface MenuItemProps {
  href: string;
  ariaLabel: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  onPress?: () => void;
}

const MenuItem: React.FC<MenuItemProps> = ({ href, ariaLabel, children, icon, onPress }) => (
  <Box href={href} aria-label={ariaLabel} onClick={onPress} className={MENU_ROW_CLASSES}>
    <MenuRowIcon>{icon}</MenuRowIcon>
    <MenuRowLabel>{children}</MenuRowLabel>
  </Box>
);

interface MenuButtonProps {
  onPress: () => void;
  label: string;
  icon?: React.ReactNode;
  analyticsName: string;
}

const MenuButton: React.FC<MenuButtonProps> = ({ onPress, label, icon, analyticsName }) => (
  <Box
    onClick={onPress}
    aria-label={label}
    analyticsName={analyticsName}
    className={MENU_ROW_CLASSES}
  >
    <MenuRowIcon>{icon}</MenuRowIcon>
    <MenuRowLabel>{label}</MenuRowLabel>
  </Box>
);

export default AccountMenu;
